import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Users, Target, Award, Heart, TrendingUp } from 'lucide-react';

const About = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Navigation */}
            <nav className="bg-white shadow-sm sticky top-0 z-50">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <Link to="/" className="flex items-center space-x-2">
                            <img
                                src="/softye_expense_tracker.png"
                                alt="Softye Logo"
                                className="w-10 h-10 object-contain"
                            />
                            <span className="text-xl font-bold text-gray-800">Softye Expense Tracker</span>
                        </Link>
                        <div className="flex items-center gap-3">
                            <Link to="/login" className="text-gray-700 hover:text-blue-600 px-4 py-2 rounded-lg font-medium transition-colors">
                                Login
                            </Link>
                            <Link to="/signup" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                                Start Free
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="container mx-auto px-4 py-16">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        Empowering MSMEs with <span className="text-blue-600">Free Financial Tools</span>
                    </h1>
                    <p className="text-xl text-gray-600 mb-8">
                        We believe every small business deserves access to professional financial management tools. That's why Softye Expense Tracker is 100% free, forever.
                    </p>
                </div>
            </section>

            {/* Our Story */}
            <section className="bg-white py-16">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
                        <div className="prose prose-lg max-w-none text-gray-600">
                            <p className="mb-4">
                                Softye Technologies was founded with a simple mission: to make professional business tools accessible to every small business in India. We noticed that while large corporations had access to expensive accounting software, MSMEs, freelancers, and small shop owners were left managing finances with pen and paper or complicated Excel sheets.
                            </p>
                            <p className="mb-4">
                                In 2024, we launched Softye Expense Tracker - a completely free, easy-to-use financial management tool designed specifically for the Indian MSME sector. Built by entrepreneurs who understand the challenges of running a small business, our platform combines simplicity with powerful features.
                            </p>
                            <p>
                                Today, thousands of small businesses across India use Softye to track their finances, manage multiple ledgers, and make data-driven decisions. And we're just getting started.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Mission & Values */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission & Values</h2>
                        <p className="text-gray-600 text-lg">What drives us every day</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                                <Target className="h-6 w-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Mission</h3>
                            <p className="text-gray-600">
                                To democratize financial management for MSMEs across India by providing free, professional-grade tools that help businesses thrive.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                                <Heart className="h-6 w-6 text-green-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Customer First</h3>
                            <p className="text-gray-600">
                                Every feature we build is designed with MSME owners in mind. Simple, intuitive, and actually useful.
                            </p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                                <Award className="h-6 w-6 text-purple-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Quality & Trust</h3>
                            <p className="text-gray-600">
                                Your financial data is precious. We use bank-level security and never compromise on quality.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why We're Free */}
            <section className="bg-blue-600 py-16 text-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-bold mb-6">Why Is Softye Free?</h2>
                        <p className="text-xl text-blue-100 mb-8">
                            We believe access to financial tools shouldn't be a privilege. Every MSME deserves to succeed.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                <CheckCircle className="h-8 w-8 mb-3" />
                                <h3 className="text-xl font-semibold mb-2">Supporting Indian Businesses</h3>
                                <p className="text-blue-100">
                                    MSMEs are the backbone of India's economy. We want to help them grow stronger.
                                </p>
                            </div>
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                <CheckCircle className="h-8 w-8 mb-3" />
                                <h3 className="text-xl font-semibold mb-2">Sustainable Model</h3>
                                <p className="text-blue-100">
                                    We build tools that help businesses succeed. When they grow, we grow together.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Team Section */}
            <section className="py-16">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Built by Entrepreneurs, for Entrepreneurs</h2>
                        <p className="text-gray-600 text-lg">
                            Our team understands the challenges of running a small business because we've been there.
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                                <div>
                                    <Users className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                                    <div className="text-3xl font-bold text-gray-900">1000+</div>
                                    <div className="text-gray-600">Active Users</div>
                                </div>
                                <div>
                                    <TrendingUp className="h-12 w-12 text-green-600 mx-auto mb-3" />
                                    <div className="text-3xl font-bold text-gray-900">50,000+</div>
                                    <div className="text-gray-600">Transactions Tracked</div>
                                </div>
                                <div>
                                    <Award className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                                    <div className="text-3xl font-bold text-gray-900">4.8/5</div>
                                    <div className="text-gray-600">User Rating</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="bg-gray-900 text-white py-16">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-4">Ready to Take Control of Your Finances?</h2>
                    <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                        Join thousands of MSMEs who trust Softye for their financial management.
                    </p>
                    <Link
                        to="/signup"
                        className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all hover:shadow-lg"
                    >
                        Start Free - No Credit Card Required
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-8 border-t border-gray-800">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="mb-4 md:mb-0">
                            <p className="text-gray-400">© 2024 Softye Technologies. All rights reserved.</p>
                        </div>
                        <div className="flex gap-6">
                            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
                            <Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link>
                            <Link to="/features" className="text-gray-400 hover:text-white transition-colors">Features</Link>
                            <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default About;
